/**
 * Portal card-image endpoints — the customer-plane twin of the console's
 * `photos` wiring (apps/console/src/views/Leads/leadDetailApi.ts). Token-scoped
 * to the caller's company. The backend serves them only to a company_admin
 * whose company has card images on; LeadDetailBody offers them only then.
 */
import type { LeadDetailPhotosApi } from '@viq-admin/screens';
import { adminFetchRaw } from './dataProvider';

/** Reads a filename from `Content-Disposition`, falling back to a generic name. */
function readFilename(response: Response, fallback: string): string {
  const cd = response.headers.get('Content-Disposition') ?? '';
  const match = cd.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

export const cardImagesApi: LeadDetailPhotosApi = {
  /** GET /v1/admin/rawphotos/:id/card-image */
  fetchImage: async (photoId: string): Promise<Blob> => {
    const res = await adminFetchRaw(`/v1/admin/rawphotos/${photoId}/card-image`);
    return res.blob();
  },

  /** GET /v1/admin/rawphotos/:id/card-image?download=1, saved via an anchor click. */
  downloadImage: async (photoId: string): Promise<void> => {
    const res = await adminFetchRaw(`/v1/admin/rawphotos/${photoId}/card-image?download=1`);
    const blob = await res.blob();
    const filename = readFilename(res, `card-image-${photoId}.jpg`);
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      try {
        a.click();
      } finally {
        document.body.removeChild(a);
      }
    } finally {
      URL.revokeObjectURL(url);
    }
  },
};
